/**
 * This file fills two needs;
 *  Maintain the polling interval for events,
 *  Push events from Redux onto the events$ stream.
 *
 * The pushing of events onto events$ is maintaining the existing API between Redux and components
 * which consume the events$ stream. Once all of the consumers of events$ have been updated
 * to consume directly from the Redux store, we can delete the events$ stream.
 *
 * The next step is to move this logic into a Redux connected component so we can more easily
 * access Redux and control the start of the event loop.
 */
import { EntityEvent, Event } from '@bitnimbus/api-v4/lib/account';
import { QueryClient } from 'react-query';
import { Subject } from 'rxjs/Subject';
import { DISABLE_EVENT_THROTTLE, INTERVAL } from 'src/constants';
import {
  getPollingInterval,
  getRequestDeadline,
  setPollingInterval,
  setRequestDeadline,
} from 'src/eventsPolling';
import { getEvents } from 'src/store/events/event.request';
import { ThunkDispatch } from 'src/store/types';
import { ApplicationStore } from './store';

export interface EventWithStore {
  event: Event;
  store: ApplicationStore;
  queryClient: QueryClient;
}

export interface EntityEventWithStore extends EventWithStore {
  event: EntityEvent;
}

export const events$ = new Subject<EventWithStore>();

let inProgress = false;

export const requestEvents = (
  store: ApplicationStore,
  queryClient: QueryClient
) => {
  inProgress = true;
  return (store.dispatch as ThunkDispatch)(getEvents())
    .then((events: Event[]) => {
      const reversed = events.reverse();

      /**
       * This feeds the stream for consumers of events$. We're simply pushing the events from the
       * request response onto the stream one at a time.
       */
      reversed.forEach((event: Event) => {
        events$.next({ event, store, queryClient });
      });
      inProgress = false;
    })
    .catch((e) => e);
};

export const startEventsInterval = (
  store: ApplicationStore,
  queryClient: QueryClient
) =>
  setInterval(
    () => {
      const now = Date.now();
      const pollIteration = getPollingInterval();
      const eventRequestDeadline = getRequestDeadline();

      if (now > eventRequestDeadline) {
        /**
         * If we're waiting on a request, set reset the pollIteration and return to prevent
         * overlapping requests.
         */
        if (inProgress) {
          /** leaving this commented out for now because I'm not sure if it'll break anything */
          // pollIteration = 1;
          return;
        }

        if (document.visibilityState !== 'visible') {
          // don't request events in an inactive/minimized tab.
          return;
        }

        requestEvents(store, queryClient);

        if (DISABLE_EVENT_THROTTLE) {
          /*
           * If throttling is disabled manually set the timeout so tests wait to query the mock data store.
           */
          setRequestDeadline(now + 500);
        } else {
          const timeout = INTERVAL * pollIteration;
          /** Update the deadline */
          setRequestDeadline(now + timeout);
          /* Update the iteration to a maximum of 16. */
          const newIteration = Math.min(pollIteration * 2, 16);
          if (pollIteration < 16) {
            setPollingInterval(newIteration);
          }
        }
      }
    },
    /* the following is the Nyquist rate for the minimum polling interval */
    INTERVAL / 2 - 1
  );
